import { darkModeOf, paletteSpec, type StudioChoice, type StyleModel } from '@base/shared';
import { Check, Redo2, Undo2, X } from 'lucide-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui.tsx';
import { BrandPanel, type StudioBrand } from './ColorParts.tsx';
import { StudioPreview } from './Preview.tsx';
import { PreviewBar, type PreviewView } from './PreviewBar.tsx';
import { ReviewStep } from './ReviewStep.tsx';
import { SystemView } from './SystemView.tsx';
import { STUDIO_STEPS, type StudioStep } from './state.ts';
import { ColorStep, FinishStep, LayoutStep } from './Steps.tsx';

/** O que a área da prévia mostra no Revisar. */
const MAIN_MODES = ['preview', 'compare', 'system'] as const;
type MainMode = (typeof MAIN_MODES)[number];

/** Quantas escolhas o desfazer guarda. */
const HISTORY = 50;

/**
 * O Estúdio de estilos (ADR 0128 e 0133): a bancada de tela inteira, como o protótipo. No topo,
 * as quatro etapas, desfazer e refazer, e os botões de quem usa o estúdio (`actions`); à
 * esquerda, as opções da etapa; à direita, a prévia grande com a barra de padrões, exemplos,
 * estado, leitura, claro ou escuro e aparelho. É controlado: recebe a escolha e avisa a nova;
 * Configuração › Estilo grava no sistema, e a entrevista do Smart Builder usa a mesma bancada.
 */
export function Studio({
  value,
  onChange,
  current,
  appName,
  logoUrl,
  readOnly = false,
  actions,
  onClose,
  brand,
  onBrandChange,
  models = [],
  onModelsChange,
}: {
  value: StudioChoice;
  onChange: (next: StudioChoice) => void;
  /** O estilo que vale agora, para a comparação da revisão. */
  current: StudioChoice | null;
  appName: string;
  logoUrl: string;
  /** Sobra da versão anterior; a prévia usa a marca do exemplo. */
  symbolUrl?: string;
  readOnly?: boolean;
  /** Botões do topo, à direita (Usar este estilo, Voltar ao de fábrica). */
  actions?: ReactNode;
  onClose?: () => void;
  /** O nome e o logo do sistema, mexidos na etapa Cor; sem eles a etapa não mostra a marca. */
  brand?: StudioBrand;
  onBrandChange?: (b: StudioBrand) => void;
  /** Meus modelos (ADR 0133, parte 5); sem `onModelsChange`, só exportar e importar arquivo. */
  models?: StyleModel[];
  onModelsChange?: (next: StyleModel[]) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const [step, setStep] = useState<StudioStep>('layout');
  const [past, setPast] = useState<StudioChoice[]>([]);
  const [future, setFuture] = useState<StudioChoice[]>([]);
  const [view, setView] = useState<PreviewView>({
    screen: 'list',
    domain: 'erp',
    state: 'normal',
    reading: 'normal',
    device: 'desk',
    dark: false,
  });
  const [peekColor, setPeekColor] = useState(false);
  const [mode, setMode] = useState<MainMode>('preview');
  const [against, setAgainst] = useState<{ name: string; studio: StudioChoice } | null>(
    current ? { name: t('style.compareNowName'), studio: current } : null,
  );
  const showing = step === 'review' ? mode : 'preview';
  const hasDark = darkModeOf(value) === 'auto' || !!paletteSpec(value.palette.id)?.dark;
  const index = STUDIO_STEPS.indexOf(step);
  const noColor = step === 'layout' && !peekColor;

  const change = useCallback(
    (next: StudioChoice) => {
      setPast((p) => [...p, value].slice(-HISTORY));
      setFuture([]);
      onChange(next);
    },
    [value, onChange],
  );
  const undo = () => {
    const prev = past[past.length - 1];
    if (!prev) return;
    setPast((p) => p.slice(0, -1));
    setFuture((f) => [value, ...f]);
    onChange(prev);
  };
  const redo = () => {
    const next = future[0];
    if (!next) return;
    setFuture((f) => f.slice(1));
    setPast((p) => [...p, value]);
    onChange(next);
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'z') return;
      e.preventDefault();
      if (e.shiftKey) redo();
      else undo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const props = { value, onChange: change, readOnly };
  const logo = brand?.logo ?? logoUrl;
  const previewBrand = brand && { name: brand.name || appName, logo };
  const iconBtn =
    'flex size-9 items-center justify-center rounded-md border border-rule text-muted hover:text-ink disabled:opacity-40';
  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-paper text-ink">
      <header className="flex h-14 shrink-0 items-center gap-4 border-b border-rule bg-sheet px-4">
        <span className="flex min-w-0 items-baseline gap-3">
          <span className="font-semibold whitespace-nowrap">{t('style.studio')}</span>
          <span className="truncate text-sm text-muted">{appName}</span>
        </span>
        <nav aria-label={t('style.stepsLabel')} className="mx-auto">
          <ol className="flex items-center gap-1">
            {STUDIO_STEPS.map((s, i) => {
              const active = s === step;
              return (
                <li key={s}>
                  <button
                    type="button"
                    aria-current={active ? 'step' : undefined}
                    onClick={() => setStep(s)}
                    className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm ${active ? 'bg-brand-soft font-medium text-brand-text' : 'text-muted hover:text-ink'}`}
                  >
                    <span
                      aria-hidden
                      className={`flex size-5 items-center justify-center rounded-full border text-xs font-semibold ${i < index ? 'border-accent bg-accent text-on-accent' : active ? 'border-current' : 'border-rule'}`}
                    >
                      {i < index ? <Check size={12} /> : i + 1}
                    </span>
                    {t(`style.steps.${s}`)}
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>
        <span className="flex items-center gap-2">
          <button
            type="button"
            className={iconBtn}
            disabled={!past.length}
            onClick={undo}
            aria-label={t('style.undo')}
            title={t('style.undo')}
          >
            <Undo2 size={16} aria-hidden />
          </button>
          <button
            type="button"
            className={iconBtn}
            disabled={!future.length}
            onClick={redo}
            aria-label={t('style.redo')}
            title={t('style.redo')}
          >
            <Redo2 size={16} aria-hidden />
          </button>
          {actions}
          {onClose && (
            <button
              type="button"
              className={iconBtn}
              onClick={onClose}
              aria-label={t('style.close')}
              title={t('style.close')}
            >
              <X size={16} aria-hidden />
            </button>
          )}
        </span>
      </header>
      <div className="flex min-h-0 flex-1">
        <aside className="flex w-[23rem] shrink-0 flex-col border-r border-rule bg-sheet">
          <div className="flex flex-col gap-1 border-b border-rule px-5 py-4">
            <span className="font-mono text-xs text-brand-text">
              {t('style.stepOf', { n: index + 1, total: STUDIO_STEPS.length })}
            </span>
            <h1 className="ui-head text-lg">{t(`style.steps.${step}`)}</h1>
            <p className="text-sm text-muted">{t(`style.stepHints.${step}`)}</p>
          </div>
          <div className="@container flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-4 [&_.ui-panel]:shadow-none">
            {step === 'layout' && <LayoutStep {...props} />}
            {step === 'color' && (
              <ColorStep
                {...props}
                logoUrl={logo}
                brand={
                  brand &&
                  onBrandChange && (
                    <BrandPanel
                      brand={brand}
                      onChange={onBrandChange}
                      defaultLogo={logoUrl}
                      placeholder={appName}
                      readOnly={readOnly}
                    />
                  )
                }
              />
            )}
            {step === 'finish' && <FinishStep {...props} />}
            {step === 'review' && (
              <ReviewStep
                value={value}
                appName={appName}
                onChange={change}
                readOnly={readOnly}
                models={models}
                onModelsChange={onModelsChange}
                onCompare={(m) => {
                  setAgainst({ name: m.name, studio: m.studio });
                  setMode('compare');
                }}
              />
            )}
          </div>
          <footer className="flex justify-between gap-2 border-t border-rule px-5 py-3">
            {index > 0 ? (
              <Button variant="secondary" onClick={() => setStep(STUDIO_STEPS[index - 1]!)}>
                {t('style.back')}
              </Button>
            ) : (
              <span />
            )}
            {index < STUDIO_STEPS.length - 1 && (
              <Button onClick={() => setStep(STUDIO_STEPS[index + 1]!)}>
                {t('style.nextTo', { step: t(`style.steps.${STUDIO_STEPS[index + 1]!}`) })}
              </Button>
            )}
          </footer>
        </aside>
        <main className="flex min-w-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
          {step === 'review' && (
            <span
              role="group"
              className="flex gap-0.5 self-start rounded-lg bg-sheet p-0.5 text-sm"
            >
              {MAIN_MODES.filter((m) => m !== 'compare' || against).map((m) => (
                <button
                  key={m}
                  type="button"
                  aria-pressed={mode === m}
                  onClick={() => setMode(m)}
                  className={`rounded-md px-3 py-1.5 ${mode === m ? 'bg-paper font-medium' : 'text-muted'}`}
                >
                  {m === 'compare' && against
                    ? t('style.compareWith', { name: against.name })
                    : t(`style.modes.${m}`)}
                </button>
              ))}
            </span>
          )}
          {showing === 'system' ? (
            <SystemView
              value={value}
              appName={appName}
              brand={previewBrand}
              view={{ ...view, dark: hasDark && view.dark }}
              onOpen={(screen) => {
                setView({ ...view, screen });
                setMode('preview');
              }}
            />
          ) : (
            <>
              <PreviewBar
                view={view}
                onChange={setView}
                hasDark={hasDark}
                noColorHint={noColor}
                onPeekColor={() => setPeekColor(true)}
              />
              {showing === 'compare' && against ? (
                <div className="grid gap-4 xl:grid-cols-2">
                  {[
                    [against.name, against.studio] as const,
                    [t('style.compareNew'), value] as const,
                  ].map(([label, s], i) => (
                    <figure key={i} className="flex min-w-0 flex-col gap-2">
                      <figcaption className="text-xs font-medium text-muted first-letter:uppercase">
                        {label}
                      </figcaption>
                      <StudioPreview
                        value={s}
                        appName={appName}
                        brand={previewBrand}
                        {...view}
                        dark={hasDark && view.dark}
                      />
                    </figure>
                  ))}
                </div>
              ) : (
                <StudioPreview
                  value={value}
                  appName={appName}
                  brand={previewBrand}
                  {...view}
                  dark={hasDark && view.dark && !noColor}
                  noColor={noColor}
                />
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
